import { ArrowRight } from "lucide-react"
import * as React from "react"
import { Link } from "react-router-dom"

import {
  decisionTone,
  formatDelta,
  formatMoment,
  matchLabel,
  ReviewView,
  RuleDiffView,
  SeonDecision,
  TONE_TEXT,
} from "@/components/seon/live-parts"
import { Badge } from "@/components/ui/badge"
import { Notice } from "@/components/ui/notice"
import { Skeleton } from "@/components/ui/skeleton"
import { getSeonLivePair, type LivePair } from "@/lib/api"
import { formatInt, formatScore } from "@/lib/format"
import { cn } from "@/lib/utils"

import { Field, Fields, Panel } from "./card-ui"

/**
 * Блок «SEON» рядом с нашим решением — только у строк двойника (AMEND-59 шаг 2).
 *
 * Отдельный запрос, а не поле карточки: карточка читает одну строку
 * `transactions`, а вебхуки лежат в своих таблицах и приходят позже решения.
 * Пока вебхука нет, блок говорит «SEON ещё не прислал» — не ноль и не прочерк
 * в месте балла: ноль читался бы как «SEON счёл транзакцию чистой».
 */
export function SeonPanel({ twinId }: { twinId: string }) {
  const [pair, setPair] = React.useState<LivePair | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    const controller = new AbortController()
    let ignore = false
    setLoading(true)
    getSeonLivePair(twinId, controller.signal)
      .then((result) => {
        if (ignore) return
        setPair(result)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (ignore || controller.signal.aborted) return
        setPair(null)
        setError(cause instanceof Error ? cause.message : "Request failed")
      })
      .finally(() => {
        if (!ignore) setLoading(false)
      })
    return () => {
      ignore = true
      controller.abort()
    }
  }, [twinId])

  const seon = pair?.seon ?? null

  return (
    <Panel
      title="SEON"
      right={
        <Link
          to="/scoring/live"
          className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
        >
          Все пары
          <ArrowRight className="h-3 w-3" strokeWidth={2.5} />
        </Link>
      }
    >
      {loading && !pair && !error && (
        <div className="space-y-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-3 w-48" />
        </div>
      )}

      {error && <Notice tone="error">Блок SEON не загрузился: {error}</Notice>}

      {pair && seon === null && (
        <div>
          <p className="text-lg font-medium leading-tight text-fg-muted">SEON ещё не прислал</p>
          <p className="mt-2 text-xs leading-relaxed text-fg-subtle">
            Вебхук с решением SEON по этой транзакции не приходил. Балл, решение и правила SEON
            появятся здесь сами, когда он придёт, — до тех пор сравнивать не с чем.
          </p>
        </div>
      )}

      {pair && seon && (
        <div className="space-y-3">
          <div className="flex items-end justify-between gap-4">
            <span
              className={cn(
                "text-4xl font-semibold leading-none",
                TONE_TEXT[decisionTone(seon.decision)],
              )}
            >
              {formatScore(seon.fraud_score)}
            </span>
            <SeonDecision seon={seon} />
          </div>

          {pair.decision_mismatch && (
            <Notice tone="warn">
              Решения расходятся: у нас {pair.ours?.state}, у SEON {seon.decision?.toUpperCase()}.
            </Notice>
          )}

          <Fields cols={2}>
            <Field label="Δ балла (мы − SEON)" value={formatDelta(pair.delta)} />
            <Field
              label="Правил врозь"
              value={pair.rules_differ === null ? null : formatInt(pair.rules_differ)}
            />
            <Field label="Вебхук получен" value={formatMoment(seon.received_at)} />
            <Field label="Склейка" value={matchLabel(pair.match)} />
            <Field
              label="Версия · подпись"
              value={
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  <span>
                    v{seon.version}
                    {seon.version > 1 ? " · переобработка" : ""}
                  </span>
                  {seon.signature_verified ? (
                    <Badge tone="approve">подписан</Badge>
                  ) : (
                    <Badge tone="review">без подписи</Badge>
                  )}
                </span>
              }
            />
          </Fields>

          <div className="border-t border-line pt-3">
            <p className="stencil-label mb-1.5 text-fg-subtle">Ручная проверка в SEON</p>
            <ReviewView review={pair.review} />
          </div>

          <div className="border-t border-line pt-3">
            <RuleDiffView rules={pair.rules} stacked />
          </div>
        </div>
      )}
    </Panel>
  )
}
